import React from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  GlobalStyles,
  Paper,
} from '@mui/material';
import { styled, type Theme } from '@mui/material/styles';

import DemoViewSwitch from '@/components/common/DemoViewSwitch';
import {
  BOTTOM_NAV_BAR_HEIGHT,
  BOTTOM_NAV_HEIGHT_VAR,
  DEMO_VIEW_BAR_HEIGHT,
  getBottomNavItems,
  type NavUser,
} from '@/config/navigation';

/**
 * The sample-student strip's height while the bar carries it.
 * `.safe-mobile-all-with-bottom-nav` (src/styles/safe-areas.css) adds it to
 * main's end padding, so the last line of a page clears the taller bar.
 */
const DEMO_VIEW_BAR_HEIGHT_VAR = '--demo-view-bar-height';

// The bar sits fixed above the page, so it tells the page how much of the
// viewport it covers: the button row (plus the sample-student strip while it
// shows) and the bottom padding `.safe-mobile-bottom` gives it. Rendered with
// the bar and scoped to the breakpoint that shows it, so the properties are
// unset wherever there is no bar and BOTTOM_NAV_OFFSET falls back to 0.
const bottomNavHeightStyles = (stripHeight: number) => (theme: Theme) => ({
  [theme.breakpoints.down('sm')]: {
    ':root': {
      [BOTTOM_NAV_HEIGHT_VAR]: `calc(${BOTTOM_NAV_BAR_HEIGHT + stripHeight}px + var(--safe-area-bottom))`,
      ...(stripHeight > 0 && {
        [DEMO_VIEW_BAR_HEIGHT_VAR]: `${stripHeight}px`,
      }),
    },
  },
});

const MobileBottomNav = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.appBar,
  display: 'block',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
  // Set explicitly rather than left to MUI's default: the published height
  // above is derived from the same constant.
  '& .MuiBottomNavigation-root': {
    height: BOTTOM_NAV_BAR_HEIGHT,
  },
  // Five buttons (four primary + More) do not fit MUI's defaults on a 320px
  // phone: the action's 80px min-width alone needs 400px, and the label jumps
  // 12→14px when selected, so the active tab shoves its neighbours off-screen.
  // Let the flex children shrink evenly and pin the label at one size.
  '& .MuiBottomNavigationAction-root': {
    minWidth: 0,
    maxWidth: 168,
    padding: '6px 4px',
  },
  '& .MuiBottomNavigationAction-label': {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
    '&.Mui-selected': {
      fontSize: '11px',
    },
  },
}));

interface MobileBottomNavigationProps {
  userRole: string;
  /**
   * Passed through to `getBottomNavItems` so the rendered list matches
   * `useNavigation`'s click-handler list. Without this, items hidden by
   * a policy rule are dropped only on the handler side, so visible-tab indexes drift past
   * what the handler sees and taps land on the wrong action.
   */
  user?: NavUser | null;
  value: number | false;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
  /**
   * A demo teacher is viewing as their sample student: the bar carries the
   * "Sample student view · Back to teacher view" strip on top, because the
   * header with the switch is hidden on phones.
   */
  demoViewActive?: boolean;
}

const MobileBottomNavigation = ({
  userRole,
  user,
  value,
  onChange,
  demoViewActive = false,
}: MobileBottomNavigationProps) => {
  const bottomNavItems = getBottomNavItems(userRole, user);

  // Don't render if no items
  if (!bottomNavItems || bottomNavItems.length === 0) {
    return null;
  }

  return (
    <MobileBottomNav elevation={3} className="safe-mobile-bottom">
      <GlobalStyles
        styles={bottomNavHeightStyles(
          demoViewActive ? DEMO_VIEW_BAR_HEIGHT : 0
        )}
      />
      {demoViewActive && <DemoViewSwitch layout="bar" />}
      <BottomNavigation value={value} onChange={onChange} showLabels>
        {bottomNavItems.map((item, index) => {
          const IconComponent = item.icon;
          const label =
            ('bottomNavLabel' in item && item.bottomNavLabel) || item.label;

          return (
            <BottomNavigationAction
              key={item.id}
              label={label}
              icon={<IconComponent />}
              // MUI only styles the active tab; screen readers need it said out
              // loud. "More" opens the drawer rather than a page, so it never
              // reports as current.
              aria-current={
                item.id !== 'more' && value === index ? 'page' : undefined
              }
            />
          );
        })}
      </BottomNavigation>
    </MobileBottomNav>
  );
};

export default MobileBottomNavigation;
